import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { tokenStore } from "@/utils/tokenStore";

// The server now requires the access token on the socket handshake and only
// pushes a user's own notifications to them. A rejected handshake (e.g. the
// access token expired while the tab was idle) is retried with backoff; the
// token is read fresh on every attempt, so the next API call's refresh fixes it.
const RETRY_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error("useWebSocket must be used inside provider");
  return ctx;
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const listenersRef = useRef(new Set());

  useEffect(() => {
    if (!user) return;

    let retryTimer = null;
    let retryIndex = 0;

    socketRef.current = io(process.env.REACT_APP_BACKEND_URL.replace("/api", ""), {
      transports: ["websocket"],
      withCredentials: true,
      auth: (cb) => cb({ token: tokenStore.get() }),
    });

    socketRef.current.on("connect", () => {
      retryIndex = 0;
      setIsConnected(true);
    });

    // Middleware rejections are not retried by socket.io itself.
    socketRef.current.on("connect_error", (err) => {
      setIsConnected(false);
      if (err?.message !== "unauthorized") return; // network errors: socket.io retries
      const delay = RETRY_DELAYS_MS[Math.min(retryIndex, RETRY_DELAYS_MS.length - 1)];
      retryIndex += 1;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => socketRef.current?.connect(), delay);
    });

    //websocketcontext
    socketRef.current.on("new_notification", (data) => {
      console.log("🔔 Notification received:", data);

      const targetUserIds = Array.isArray(data.userId)
        ? data.userId.map(String)
        : [String(data.userId)];

      // Employees are addressed by their Employee id, admins by their User
      // id — and either by both (an employee who manages people is notified
      // on their login; an admin who is also an employee on their record).
      const ownIds = [String(user.id), user.employee_id && String(user.employee_id)].filter(Boolean);

      if (!targetUserIds.some((id) => ownIds.includes(id))) {
        return;
      }

      listenersRef.current.forEach(cb => cb(data.data));
    });

    socketRef.current.on("disconnect", () => {
      console.log("❌ Socket.IO disconnected");
      setIsConnected(false);
    });

    return () => {
      clearTimeout(retryTimer);
      socketRef.current.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  const addNotificationListener = (cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  };

  // Generic room + event helpers so features can receive pushed updates
  // (e.g. payslip email job progress) instead of polling.
  const joinRoom = (room) => {
    if (room) socketRef.current?.emit("joinRoom", { room });
  };

  const leaveRoom = (room) => {
    if (room) socketRef.current?.emit("leaveRoom", { room });
  };

  // Subscribe to a socket event; returns an unsubscribe fn.
  const subscribe = (event, cb) => {
    const socket = socketRef.current;
    if (!socket) return () => {};
    socket.on(event, cb);
    return () => socket.off(event, cb);
  };

  return (
    <WebSocketContext.Provider
      value={{ isConnected, addNotificationListener, joinRoom, leaveRoom, subscribe }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

